import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKeyValid, setApiKeyValid] = useState(true);
  const [checkingTelegramAuth, setCheckingTelegramAuth] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Проверяем Telegram данные сразу при загрузке
  useEffect(() => {
    const checkTelegramAuthOnLoad = () => {
      const telegramData = localStorage.getItem('telegram_auth_data');
      if (telegramData && !user) {
        console.log('🔄 Found Telegram auth data, processing...');
        setCheckingTelegramAuth(true);
        handleStoredTelegramAuth(telegramData);
      }
    };

    if (!loading) {
      checkTelegramAuthOnLoad();
    }
  }, [loading, user]);

  const handleStoredTelegramAuth = async (telegramData) => {
    try {
      const parsedData = JSON.parse(telegramData);
      console.log('📨 Processing stored Telegram data:', parsedData);
      
      const result = await loginWithTelegram(parsedData);
      
      if (result.success) {
        localStorage.removeItem('telegram_auth_data');
        console.log('✅ Stored Telegram auth successful');
      } else {
        console.error('❌ Stored Telegram auth failed:', result.error);
        localStorage.removeItem('telegram_auth_data');
      }
    } catch (error) {
      console.error('❌ Stored Telegram auth error:', error);
      localStorage.removeItem('telegram_auth_data');
    } finally {
      setCheckingTelegramAuth(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const userData = await authAPI.getCurrentUser();
        const enrichedUserData = {
          ...userData,
          name: userData.name || 'Пользователь',
          email: userData.email || '',
          role: userData.role || 'user',
          is_active: userData.is_active !== undefined ? userData.is_active : true,
          created_at: userData.created_at || null,
          updated_at: userData.updated_at || null
        };
        setUser(enrichedUserData);
        setApiKeyValid(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        
        if (error.response?.status === 403 && error.response?.data?.detail === 'Invalid API Key') {
          setApiKeyValid(false);
        } else if (error.response?.status === 401) {
          // 401 обрабатывается в интерцепторе, здесь просто очищаем состояние
          logout();
        } else {
          console.error('Auth check error:', error);
        }
      }
    }
    setLoading(false);
  };

  const login = async (email, password, useJson = true) => {
  try {
    let response;
    if (useJson) {
      response = await authAPI.loginJson(email, password);
    } else {
      response = await authAPI.login(email, password);
    }

    const { access_token } = response;
    localStorage.setItem('access_token', access_token);
    
    const userData = await authAPI.getCurrentUser();
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    
    return { success: true };
  } catch (error) {
    console.error('Login error in context:', error);
    
    // Если это ошибка валидации, пробрасываем ее как есть
    if (error.response?.status === 422) {
      const fieldErrors = extractFieldErrors(error.response.data);
      return { 
        success: false, 
        error: 'Ошибка валидации данных',
        fieldErrors: fieldErrors
      };
    }
    
    // Для других ошибок возвращаем сообщение
    const message = error.response?.data?.detail || 
                   error.response?.data?.message || 
                   'Ошибка авторизации';
    return { success: false, error: message };
  }
};

  // Вспомогательная функция для извлечения ошибок полей
  const extractFieldErrors = (errorData) => {
    const fieldErrors = {};
    
    if (errorData.detail && Array.isArray(errorData.detail)) {
      errorData.detail.forEach(error => {
        if (error.loc && error.loc[1]) {
          const fieldName = error.loc[1];
          fieldErrors[fieldName] = error.msg;
        }
      });
    }
    
    return fieldErrors;
  };

  const loginWithTelegram = async (telegramData) => {
    try {
      console.log('📨 Sending Telegram data to backend:', telegramData);
      
      const processedData = {
        id: parseInt(telegramData.id),
        first_name: telegramData.first_name,
        last_name: telegramData.last_name || '',
        username: telegramData.username || '',
        photo_url: telegramData.photo_url || '',
        auth_date: parseInt(telegramData.auth_date),
        hash: telegramData.hash
      };
      
      console.log('📤 Processed data for backend:', processedData);
      
      const response = await authAPI.verifyTelegramAuth(processedData);
      console.log('✅ Backend response:', response);
      
      const { access_token } = response;
      localStorage.setItem('access_token', access_token);
      
      const userData = await authAPI.getCurrentUser();
      console.log('✅ User data received:', userData);
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return { success: true };
    } catch (error) {
      console.error('❌ Telegram auth error:', error);
      console.error('❌ Error response:', error.response);
      
      const message = error.response?.data?.detail || 
                    error.response?.data?.message || 
                    'Telegram authentication failed';
      return { success: false, error: message };
    }
  };

  const getTelegramAuthUrl = async () => {
    try {
      const response = await authAPI.getTelegramAuthUrl();
      return { success: true, url: response.auth_url };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to get Telegram auth URL';
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password) => {
    try {
      await authAPI.register(name, email, password);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('telegram_auth_data');
    setUser(null);
  };

  const value = {
    user,
    login,
    loginWithTelegram,
    getTelegramAuthUrl,
    register,
    logout,
    loading: loading || checkingTelegramAuth,
    isAuthenticated: !!user,
    apiKeyValid,
    checkingTelegramAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};